import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Building2,
  ShieldCheck,
  Rocket,
  Settings2,
  Megaphone,
  Check,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo, { buildBreadcrumbs } from "@/components/Seo";

// ─────────────────────────────────────────────────────────
// Custom "GO" mark — inline SVG so it stays crisp, brand-safe,
// and works offline. Big cyan "GO" with an upward arrow whip.
// ─────────────────────────────────────────────────────────
function GoMark({ className = "" }) {
  return (
    <svg
      viewBox="0 0 640 260"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="GO — Start, build or re-imagine your brand"
      data-testid="go-mark"
    >
      <defs>
        <linearGradient id="goFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#67E8F9" />
          <stop offset="55%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#0EA5E9" />
        </linearGradient>
        <linearGradient id="goShade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0891B2" />
          <stop offset="100%" stopColor="#164E63" />
        </linearGradient>
        <linearGradient id="arrowFill" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#67E8F9" />
        </linearGradient>
      </defs>

      {/* Letter G — thick sans, open-mouth right */}
      <path
        d="M170 24 C88 24 24 88 24 178 C24 234 66 260 130 260 C182 260 232 230 232 172 L232 138 L142 138 L142 176 L182 176 L182 194 C182 216 158 226 132 226 C96 226 72 208 72 176 C72 118 108 66 174 66 C202 66 220 74 234 90 L266 60 C246 36 214 24 170 24 Z"
        fill="url(#goFill)"
      />
      {/* Letter G — subtle shade slice on the underside */}
      <path
        d="M170 24 C110 24 60 60 34 116 L78 132 C98 92 132 66 174 66 C202 66 220 74 234 90 L266 60 C246 36 214 24 170 24 Z"
        fill="url(#goShade)"
        opacity="0.35"
      />

      {/* Letter O — circular with slight tilt */}
      <path
        d="M394 24 C316 24 258 84 258 168 C258 226 300 260 366 260 C444 260 502 200 502 116 C502 58 460 24 394 24 Z M390 66 C428 66 454 84 454 122 C454 178 424 218 372 218 C334 218 306 200 306 162 C306 106 336 66 390 66 Z"
        fill="url(#goFill)"
      />

      {/* Upward-swooping arrow that erupts from the O */}
      <path
        d="M470 190 C520 172 566 138 596 96 L568 78 L620 42 L628 106 L602 88 C566 138 516 176 462 200 Z"
        fill="url(#arrowFill)"
      />
      {/* Arrow whisker (motion trail) */}
      <path
        d="M448 214 C494 208 538 190 578 158"
        stroke="url(#arrowFill)"
        strokeWidth="8"
        strokeLinecap="round"
        fill="none"
        opacity="0.55"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────
// Data — categories & services, verbatim from client copy
// ─────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "formation",
    kicker: "01",
    icon: Building2,
    title: "Business Formation & Strategy Planning",
    body: "The task of identifying the necessary local, state and federal procedures to successfully register your business is managed effectively.",
    services: [
      "Name Search Availability",
      "LLC, Partnership, Corporation",
      "Strategic Engagement Planning",
      "Local, State & Regional registration",
      "Registration Certificate",
      "12-Point Business Readiness Assessment",
    ],
  },
  {
    id: "trademark",
    kicker: "02",
    icon: ShieldCheck,
    title: "Trademark Search & Registration",
    body: "The search, submission, and management of your trademark or service mark application process are handled until approval is secured.",
    services: [
      "Database Name Search",
      "Application, Status & Monitoring",
      "Office Actions",
      "Certificate of Registration",
      "Submission Renewals",
    ],
  },
  {
    id: "brand-launch",
    kicker: "03",
    icon: Rocket,
    title: "Brand Launch Services",
    body: "While brainstorming your new business idea, our team can provide insightful concepts and outstanding suggestions.",
    services: [
      "Story Board Development",
      "Website Creation & Development",
      "New Logos, Image, or Brand Refresh",
      "Social Media Management",
      "Editorial Design",
    ],
  },
  {
    id: "operations",
    kicker: "04",
    icon: Settings2,
    title: "Operations Support",
    body: "Assistance is available for challenging operational tasks through reviewing, discussing and recommending strategies.",
    services: [
      "Strategy Road-Map Planning",
      "Risk Management",
      "Enterprise CMS / CRM",
      "Project Management Software",
      "Shopify Management",
      "Business Plan Support",
    ],
  },
  {
    id: "event-marketing",
    kicker: "05",
    icon: Megaphone,
    title: "Event Marketing & Support",
    body: "Growth-revenue engine for businesses, conferences, and tradeshow owners — strategy, branding, social media, marketing, and attendee reach engineered to move the numbers that matter.",
    services: [
      "Growth & Revenue Strategy",
      "Brand Identity & Positioning",
      "Social Media Management",
      "Paid & Organic Marketing",
      "Attendee Acquisition & Reach",
      "Sponsorship & Exhibitor Sales Support",
      "Content & Editorial",
      "Analytics & Post-Event Reporting",
    ],
    learnPath: "/services/event-marketing",
  },
];

// ─────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────
export default function B2bServices() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="B2B Services — Smart, Scalable Hospitality-Growth Strategies"
        description="Drive revenue with smart, scalable hospitality-growth strategies. Business formation, trademark, brand launch, and operations support — full-service or hourly, retained to grow with your brand."
        path="/b2b-services"
        keywords="hospitality growth, B2B services, business formation, trademark registration, brand launch, operations support, account management, hospitality strategies"
        jsonLd={buildBreadcrumbs([
          ["Home", "/"],
          ["B2B Services", "/b2b-services"],
        ])}
      />

      {/* ============ HERO ============ */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[560px] h-[560px] -top-32 -right-24 opacity-40" />
        <div className="brand-bloom brand-bloom-warm w-[420px] h-[420px] -bottom-32 -left-24 opacity-30" />
        <HexMark
          size={240}
          opacity={0.06}
          className="absolute -left-10 top-28 rotate-[10deg] pointer-events-none hidden md:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                B2B Services · Hospitality-Growth Strategies
              </span>
            </div>
            <MaskedReveal
              lines={[
                "Drive Revenue With",
                "Smart, Scalable",
                "Hospitality-Growth Strategies.",
              ]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[10.5vw] md:text-[6.4vw] lg:text-[5.4vw]"
              stagger={0.09}
            />
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                We help hospitality brands, companies &amp; associations to grow
                with confidence.{" "}
                <span className="text-[var(--text)] font-medium italic">
                  As a full service or hourly solution, our account management
                  services are available to support your vision.
                </span>{" "}
                Outsource any task — small or large.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="b2b-hero-cta-quote">
                  Request a Quote <ArrowRight size={14} />
                </Link>
                <a
                  href="#services"
                  className="btn-dark"
                  data-testid="b2b-hero-cta-explore"
                >
                  Explore Services <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ GO MARK / MANIFESTO (dark) ============ */}
      <section
        aria-label="Start, Build or Re-Imagine Your Brand"
        className="relative py-24 md:py-36 overflow-hidden bg-[#050A14] text-white"
        data-testid="b2b-go-section"
      >
        <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[560px] h-[560px] rounded-full bg-sky-500/15 blur-3xl" />
        <HexMark
          size={280}
          opacity={0.06}
          className="absolute right-6 top-16 rotate-[16deg] pointer-events-none hidden lg:block"
        />
        <span className="corner-cross" style={{ top: 40, left: 40, color: "#fff" }} />
        <span className="corner-cross" style={{ bottom: 40, right: 40, color: "#fff" }} />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-14 items-center">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <motion.div
                initial={{ opacity: 0, scale: 0.94, rotate: -2 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-[420px]"
              >
                <GoMark className="w-full h-auto drop-shadow-[0_20px_50px_rgba(34,211,238,0.25)]" />
              </motion.div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <Reveal delay={0.15}>
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <Sparkles size={12} /> Start · Build · Re-Imagine
              </span>
            </Reveal>
            <Reveal delay={0.25}>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.96] tracking-[-0.03em]">
                Start, Build or{" "}
                <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                  Re-Imagine Your Brand.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="mt-8 text-white/75 text-lg leading-relaxed max-w-lg">
                Helping clients build, grow, and achieve business success is a
                passion. From formation to launch to daily operations — one team,
                accountable end-to-end.
              </p>
            </Reveal>
            <Reveal delay={0.45}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="b2b-go-cta-quote">
                  Start a conversation <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact#book"
                  className="btn-dark backdrop-blur"
                  data-testid="b2b-go-cta-book"
                >
                  Book a 15-min call <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ SERVICE CATEGORIES GRID ============ */}
      <section
        id="services"
        className="relative py-20 md:py-32 overflow-hidden scroll-mt-28"
      >
        <div className="absolute inset-0 hex-pattern-dense opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[480px] h-[480px] top-24 -right-32 opacity-35" />
        <div className="brand-bloom brand-bloom-cyan w-[420px] h-[420px] bottom-24 -left-24 opacity-30" />
        <HexMark
          size={220}
          opacity={0.055}
          className="absolute -right-10 bottom-28 -rotate-[14deg] pointer-events-none hidden lg:block"
        />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Four Services
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  From formation to{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    the day you scale.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Every service can be booked hourly, retained as a scope of work,
                or embedded into your team full-service.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {CATEGORIES.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 0.08}>
                <article
                  data-testid={`b2b-category-${cat.id}`}
                  className="group relative card-white overflow-hidden h-full p-6 md:p-10 hover:border-[var(--accent)]/50 hover:shadow-[0_24px_60px_-30px_rgba(6,182,212,0.4)] transition-all duration-500"
                >
                  <span className="corner-cross" style={{ top: 14, right: 14 }} />
                  {/* GO arrow ghost mark on hover */}
                  <div className="pointer-events-none absolute -right-6 -bottom-6 w-40 h-40 opacity-[0.05] group-hover:opacity-[0.09] transition-opacity duration-700">
                    <GoMark className="w-full h-full" />
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="icon-tile shrink-0">
                      <cat.icon size={22} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                        Service · {cat.kicker}
                      </span>
                      <h3 className="mt-2 font-editorial text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                        {cat.title}
                      </h3>
                    </div>
                    <div className="numeral-mono text-4xl md:text-5xl select-none opacity-90 shrink-0">
                      /{cat.kicker}
                    </div>
                  </div>

                  <p className="mt-6 text-[15px] leading-relaxed text-[var(--text-muted)] max-w-xl">
                    {cat.body}
                  </p>

                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {cat.services.map((s) => (
                      <li
                        key={s}
                        className="flex items-start gap-3 text-[14px] leading-snug text-[var(--text)]/85"
                      >
                        <span className="mt-1.5 shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-cyan-400 to-sky-600 text-white shadow">
                          <Check size={9} strokeWidth={3} />
                        </span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 pt-6 border-t border-[var(--border)] flex items-center justify-between">
                    <Link
                      to={cat.learnPath || "/contact"}
                      data-testid={`b2b-category-cta-${cat.id}`}
                      className="inline-flex items-center gap-2 font-mono-brand text-[11px] uppercase tracking-[0.22em] text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                    >
                      {cat.learnPath ? "Explore service" : "Get scoped"} <ArrowRight size={14} />
                    </Link>
                    <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] hidden md:inline">
                      /ref · {cat.kicker}
                    </span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLOSING CTA ============ */}
      <section className="relative py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden p-8 md:p-16">
            <div className="absolute inset-0 grid-pattern-dark opacity-30" />
            <div className="absolute -top-24 -left-20 w-[380px] h-[380px] rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-20 w-[420px] h-[420px] rounded-full bg-sky-500/20 blur-3xl" />
            <div className="relative grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 lg:col-span-8 text-white">
                <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                  <span className="chip-dot" /> Ready when you are
                </span>
                <h3 className="mt-6 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em]">
                  Outsource any task —{" "}
                  <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                    small or large.
                  </span>
                </h3>
                <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
                  A scoped conversation within one business day. Hourly, retained,
                  or embedded — your call.
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3">
                <Link to="/contact" className="btn-primary justify-center sm:justify-start w-full sm:w-auto" data-testid="b2b-footer-cta-quote">
                  Request a Quote <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact#book"
                  className="btn-dark backdrop-blur justify-center sm:justify-start w-full sm:w-auto"
                  data-testid="b2b-footer-cta-book"
                >
                  Book a call <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
